import React from "react";
import { Link } from "react-router-dom";
import { Smartphone, Tablet, Laptop, LaptopMinimal, ArrowRight, CheckCircle2, ShieldCheck, Download, Mail, Tv } from "lucide-react";
import PaymentSecurity from "../components/PaymentSecurity";
import { RequestManual } from "../components/RequestManual";
import { Seo, orgJsonLd, BASE } from "../components/Seo";

const HERO = "https://images.unsplash.com/photo-1758691030817-a6271a533c42?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

const CATS = [
  { slug: "iphone", name: "iPhone", Icon: Smartphone, desc: "Lär dig din telefon steg för steg." },
  { slug: "ipad", name: "iPad", Icon: Tablet, desc: "Surfa, ring och läs med lugn." },
  { slug: "macbook", name: "MacBook", Icon: LaptopMinimal, desc: "Lär känna din Apple-dator." },
  { slug: "windows11", name: "Windows 11", Icon: Laptop, desc: "Förstå din dator på riktigt." },
];

export default function Home() {
  return (
    <div>
      <Seo
        title="Enkla manualer och IT-hjälp på svenska"
        description="Pedagogiska manualer i PDF för iPhone, iPad, MacBook och Windows 11 – plus hemkurser, enhetsbyte, digitala lås och tips för att surfa säkert. Gjorda för alla åldrar."
        path="/"
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "WebSite",
            name: "Enkla Manualer",
            url: BASE,
            inLanguage: "sv-SE",
          },
          orgJsonLd,
        ]}
      />
      {/* Hero */}
      <section className="bg-white border-b-2 border-gray-200">
        <div className="max-w-5xl mx-auto px-6 md:px-12 py-12 md:py-20 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <h1 className="font-heading text-4xl md:text-5xl font-extrabold tracking-tight text-[#1A1A1A] leading-tight">
              Tydliga manualer som gör tekniken enkel
            </h1>
            <p className="text-lg md:text-xl leading-relaxed text-[#4A4A4A] mt-6">
              Pedagogiska guider i PDF-format för iPhone, iPad, MacBook och Windows 11.
            </p>
            <p className="text-lg md:text-xl leading-relaxed text-[#4A4A4A] mt-4">
              Stor text, tydliga bilder och lugna förklaringar – gjorda för alla åldrar.
            </p>
            <a
              href="#kategorier"
              data-testid="hero-cta"
              className="inline-flex items-center justify-center gap-3 min-h-[64px] px-8 mt-8 bg-[#0056D2] text-white text-xl font-bold rounded-lg hover:bg-[#003E99] transition-colors"
            >
              Välj din manual <ArrowRight size={26} strokeWidth={2.5} />
            </a>
          </div>
          <div>
            <img
              src={HERO}
              alt="Äldre person använder en surfplatta och ler"
              className="w-full h-72 md:h-96 object-cover rounded-2xl border-2 border-gray-200"
            />
          </div>
        </div>
      </section>

      {/* Categories */}
      <section id="kategorier" className="max-w-5xl mx-auto px-6 md:px-12 py-14 md:py-20">
        <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#1A1A1A]">Vad vill du lära dig?</h2>
        <p className="text-lg text-[#4A4A4A] mt-3">Tryck på en av knapparna nedan för att komma igång.</p>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8 mt-10">
          {CATS.map(({ slug, name, Icon, desc }) => (
            <div
              key={slug}
              data-testid={`category-card-${slug}`}
              className="bg-white border-2 border-gray-300 rounded-xl p-8 flex flex-col items-center text-center hover:border-[#0056D2] transition-colors"
            >
              <span className="bg-blue-50 text-[#0056D2] rounded-2xl p-5">
                <Icon size={48} strokeWidth={2} />
              </span>
              <h3 className="font-heading text-2xl md:text-3xl font-bold text-[#1A1A1A] mt-5">{name}</h3>
              <p className="text-base text-[#4A4A4A] mt-2">{desc}</p>
              <Link
                to={`/manual/${slug}`}
                data-testid={`view-category-${slug}`}
                className="inline-flex items-center justify-center gap-2 min-h-[64px] w-full px-6 mt-6 bg-[#0056D2] text-white text-xl font-bold rounded-lg hover:bg-[#003E99] transition-colors"
              >
                Se manualen <ArrowRight size={24} strokeWidth={2.5} />
              </Link>
            </div>
          ))}
        </div>
      </section>

      {/* Smart-TV home course */}
      <section className="max-w-5xl mx-auto px-6 md:px-12 pb-4">
        <div className="bg-[#0056D2] text-white rounded-2xl p-8 md:p-12 flex flex-col md:flex-row md:items-center gap-8">
          <span className="bg-white/15 rounded-2xl p-5 self-start">
            <Tv size={56} strokeWidth={2} />
          </span>
          <div className="flex-1">
            <h2 className="font-heading text-3xl md:text-4xl font-bold">Boka hemkurs hemma hos dig</h2>
            <p className="text-lg md:text-xl text-blue-50 mt-3 leading-relaxed">
              Vill du hellre ha personlig hjälp?
            </p>
            <p className="text-lg md:text-xl text-blue-50 mt-4 leading-relaxed">
              Vi erbjuder två kurser på plats i ditt hem – en för din Smart-TV och en för din TV-box.
            </p>
          </div>
          <Link
            to="/hemkurs"
            data-testid="home-course-cta"
            className="inline-flex items-center justify-center gap-3 min-h-[64px] px-8 bg-white text-[#0056D2] text-xl font-bold rounded-lg hover:bg-blue-50 transition-colors whitespace-nowrap"
          >
            Läs mer och boka <ArrowRight size={26} strokeWidth={2.5} />
          </Link>
        </div>
      </section>

      {/* Mobile switch service CTA */}
      <section className="max-w-5xl mx-auto px-6 md:px-12 pb-4">
        <div className="bg-white border-2 border-[#0056D2] rounded-2xl p-8 md:p-12 flex flex-col md:flex-row md:items-center gap-8">
          <span className="bg-blue-50 text-[#0056D2] rounded-2xl p-5 self-start">
            <Smartphone size={56} strokeWidth={2} />
          </span>
          <div className="flex-1">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#1A1A1A]">Enkelt enhetsbyte</h2>
            <p className="text-lg md:text-xl text-[#4A4A4A] mt-3 leading-relaxed">
              Har du köpt en ny telefon eller dator? Jag flyttar över allt tryggt och säkert – appar,
              BankID, filer, kontakter och bilder. Fast pris 599 kr.
            </p>
          </div>
          <Link
            to="/enhetsbyte"
            data-testid="home-mobile-cta"
            className="inline-flex items-center justify-center gap-3 min-h-[64px] px-8 bg-[#0056D2] text-white text-xl font-bold rounded-lg hover:bg-[#003E99] transition-colors whitespace-nowrap"
          >
            Läs mer och boka <ArrowRight size={26} strokeWidth={2.5} />
          </Link>
        </div>
      </section>

      {/* How it works */}
      <section className="bg-white border-t-2 border-gray-200">
        <div className="max-w-5xl mx-auto px-6 md:px-12 py-14 md:py-20">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#1A1A1A]">Så enkelt är det</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 mt-10">
            {[
              { Icon: CheckCircle2, t: "1. Välj manual", d: "Tryck på iPhone, iPad eller Windows 11." },
              { Icon: ShieldCheck, t: "2. Betala tryggt", d: "Ange din e-post och betala med kort. Inget krångligt konto behövs." },
              { Icon: Download, t: "3. Ladda ner direkt", d: "PDF:en laddas ner med en gång och skickas även till din e-post." },
            ].map(({ Icon, t, d }) => (
              <div key={t} className="flex flex-col items-start">
                <span className="bg-green-50 text-[#0B7A3F] rounded-xl p-4">
                  <Icon size={40} strokeWidth={2} />
                </span>
                <h3 className="font-heading text-2xl font-bold text-[#1A1A1A] mt-4">{t}</h3>
                <p className="text-lg text-[#4A4A4A] mt-2">{d}</p>
                {t.startsWith("2.") && <PaymentSecurity align="left" className="mt-4" />}
              </div>
            ))}
          </div>
        </div>
      </section>

      <RequestManual />
    </div>
  );
}
